/**
 * GroupAvatar — circular composite avatar for group conversations.
 * Uses the custom group photo when one exists, otherwise tiles up to four
 * members (photo, or their initial) the way mobile's GroupAvatar does.
 */
import { useEffect, useState } from 'react';
import { selectCollageMembers } from '@wlb/shared/utils/groupConversation';
import type { ConversationMember } from '@wlb/shared/types/chat';
import { avatarFallback, avatarInitial } from '../../components/ui/avatarFallback';
import { resolveAvatarSrc } from './avatarSource';
import '../../components/ui/avatar.css';
import './messages.css';

interface GroupAvatarProps {
  name: string;
  avatar?: unknown;
  members?: ConversationMember[];
  /** Newest-first sender ids; leads the tile order when messages are loaded. */
  recentSenderIds?: Array<string | number>;
  variant?: 'list' | 'header' | 'details';
}

function CollageCell({ member }: { member: ConversationMember }) {
  const src = resolveAvatarSrc(member.avatar);
  const { initial, tone } = avatarFallback({ seed: member.profileUserGuid ?? member.id, name: member.name });
  const [failed, setFailed] = useState(false);

  useEffect(() => setFailed(false), [src]);

  if (src && !failed) {
    return (
      <img
        className="group-avatar__cell group-avatar__image"
        src={src}
        alt=""
        aria-hidden
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <span className="group-avatar__cell group-avatar__initial" data-avatar-tone={tone} aria-hidden>
      {initial}
    </span>
  );
}

export function GroupAvatar({
  name,
  avatar,
  members = [],
  recentSenderIds,
  variant = 'list',
}: GroupAvatarProps) {
  const groupPhoto = resolveAvatarSrc(avatar);
  const tiles = groupPhoto ? [] : selectCollageMembers(members, recentSenderIds);
  const count = groupPhoto ? 1 : Math.max(tiles.length, 1);

  return (
    <div className={`group-avatar group-avatar--${variant} group-avatar--count-${count}`}>
      {groupPhoto ? (
        <img
          className="group-avatar__cell group-avatar__image"
          src={groupPhoto}
          alt=""
          aria-hidden
          loading="lazy"
          decoding="async"
        />
      ) : tiles.length > 0 ? (
        tiles.map(member => <CollageCell key={member.id} member={member} />)
      ) : (
        // A group with no members to tile keeps the neutral group treatment:
        // it is not a person, so it gets no personal tone.
        <span className="group-avatar__cell group-avatar__initial" aria-hidden>
          {avatarInitial(name)}
        </span>
      )}
    </div>
  );
}
